import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useDebounce } from 'use-debounce';
import { LOAD_STATUS } from 'appConstants';
import {
  clearIconFinder,
  getIconsCategories,
  getIconsStyles,
  searchIcons,
  setIconFinderNextPage,
  setIconFinderSearchTerm,
} from 'store/actions';
import { useDispatch, useSelector } from 'react-redux';
import {
  iconFinderSelector,
  iconsCategoriesSelector,
  iconsStylesSelector,
} from 'store/selectors';

export const useIconFinder = ({ vector = 'all', enabled = true } = {}) => {
  const state = useSelector(iconFinderSelector);
  const iconsCategories = useSelector(iconsCategoriesSelector);
  const iconsStyles = useSelector(iconsStylesSelector);

  const dispatch = useDispatch();
  const lastFetchedRef = useRef(null);
  const metaFetchedRef = useRef(false);

  const debouncedValue = useMemo(
    () => ({
      searchTerm: state.searchTerm,
      offset: state.offset,
      category: state.category,
      style: state.style,
    }),
    [state.offset, state.searchTerm, state.category, state.style],
  );

  // debouncing offset to prevent bugs when user clears input
  // when user clears input, offset sets to 1 and because of this
  // effect is running with old debounced searchTerm and offset = 0
  const [{ searchTerm, offset, category, style }] = useDebounce(
    debouncedValue,
    500,
  );

  // Clear icon-finder state only when the component unmounts (i.e. the modal closes).
  useEffect(() => {
    return () => {
      dispatch(clearIconFinder());
    };
  }, [dispatch]);

  // Load categories/styles once per modal session, only when the tab becomes active.
  // The ref resets on unmount, so closing and reopening the modal fetches them again.
  useEffect(() => {
    if (!enabled || metaFetchedRef.current) {
      return;
    }

    dispatch(getIconsCategories());
    dispatch(getIconsStyles());
    metaFetchedRef.current = true;
  }, [enabled, dispatch]);

  // Trigger icon search only when the tab is active and the params have changed.
  // Results are cached in Redux while the modal stays open.
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const paramsKey = JSON.stringify({
      searchTerm,
      offset,
      category,
      style,
      count: state.count,
      vector,
    });

    // Already fetched these params for the current modal session.
    if (lastFetchedRef.current === paramsKey) {
      return;
    }

    const promise = dispatch(
      searchIcons({
        query: searchTerm || '',
        vector,
        offset,
        count: state.count,
        category,
        style,
        premium: false,
        license: `commercial-nonattribution`,
      }),
    );

    lastFetchedRef.current = paramsKey;

    return () => {
      if (promise) {
        promise.abort();
      }
    };
  }, [
    enabled,
    state.count,
    searchTerm,
    offset,
    vector,
    dispatch,
    category,
    style,
  ]);

  const setSearchTerm = useCallback(
    (searchTerm, category, style) => {
      dispatch(
        setIconFinderSearchTerm({
          searchTerm,
          category,
          style,
          // setting status manually to show loader immediately for better UX
          status: LOAD_STATUS.LOADING,
        }),
      );
    },
    [dispatch],
  );

  const loadNextPage = useCallback(() => {
    dispatch(setIconFinderNextPage());
  }, [dispatch]);

  return {
    state,
    setSearchTerm,
    loadNextPage,
    iconsCategories,
    iconsStyles,
  };
};
